import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IUserImportMeta,
  UserImportStatus
} from '@dadohr/shared/domainTypes/user';
import { FormControlLabel, Switch, Typography } from '@material-ui/core';
import { compact, sortBy as sortByLodash } from 'lodash';
import { useMemo, useState } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../components/AdditionalActionsMenu';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer
} from '../../../../components/GroupableList';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { useSort } from '../../../../hooks/useSort';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { removeDoc } from '../../../../services/firebase';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { formatDatePrecise, toMoment } from '../../../../services/time';
import {
  commitImport,
  useImportsByOrgId
} from '../../../../services/user/import';
import { useUsersSideNav } from '../sideNav';
import { EmptyState } from './components/EmptyState';
import {
  SettingsButton,
  SyncButton
} from './components/ImportIntegrationButton';
import { ImportSourceLogo } from './components/ImportSourceLogo';
import { ImportStatusChip } from './components/ImportStatusChip';

type ColumnName =
  | 'createdAt'
  | 'source'
  | 'status'
  | 'total'
  | 'added'
  | 'changed'
  | 'removed'
  | 'issues'
  | 'totalChanges'
  | 'effects'
  | 'doneAt'
  | 'actions';

type D = Doc<IUserImportMeta>;
type O = { newestImportId: string | null };
type Column = IColumn<D, ColumnName, O>;

const Actions = ({ d, o }: { d: D; o: O }) => {
  const { t } = useUnsafeTranslation();
  const options: AdditionActionsMenuOption[] = useMemo(
    () =>
      compact([
        {
          key: 'commit',
          label: t('import.actions.commit'),
          disabled:
            d.data.status !== UserImportStatus.PREVIEW ||
            d.id !== o.newestImportId,
          onClick: () => commitImport(d.data.orgId, d.id)
        },
        {
          key: 'delete',
          label: t('import.actions.delete'),
          onClick: () => removeDoc(d)
        }
      ]),
    [d, o, t]
  );
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'createdAt',
    head: () => 'Created',
    cell: (d) => formatDatePrecise(toMoment(d.data.createdAt)),
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 180,
    flexGrow: 0
  },
  {
    key: 'doneAt',
    head: () => 'Applied',
    cell: (d) =>
      d.data.doneAt ? formatDatePrecise(toMoment(d.data.doneAt)) : null,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 180,
    flexGrow: 0
  },
  {
    key: 'source',
    head: () => 'Sources',
    cell: (d) => (
      <FlexContainer>
        {d.data.sources.map((s) => (
          <ImportSourceLogo key={s.id} identifier={s} />
        ))}
      </FlexContainer>
    ),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d, o) => {
      return (
        <ImportStatusChip
          status={d.data.status}
          outdated={
            d.data.status === UserImportStatus.PREVIEW &&
            o.newestImportId !== d.id
          }
          size="small"
        />
      );
    },
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 0
  },
  {
    key: 'totalChanges',
    head: () => 'Changed Users',
    cell: (d) => {
      const s = d.data.stats;
      return s.added + s.changed + s.removed;
    },
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 125,
    flexGrow: 0
  },
  {
    key: 'issues',
    head: () => 'Problems',
    cell: (d) => d.data.stats.issues,
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 110,
    flexGrow: 0
  },
  {
    key: 'effects',
    head: () => 'Effects',
    cell: (d) => d.data.stats.effects,
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 110,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d, o) => <Actions d={d} o={o} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0,
    showOverflowOnHover: false
  }
];

const SORTERS: ItemSorters<D, O> = {
  createdAt: {
    key: 'createdAt',
    items: { sort: (d) => d.data.createdAt.toMillis(), dir: 'desc' }
  },
  doneAt: {
    key: 'doneAt',
    items: {
      sort: (d) => (d.data.doneAt ? d.data.doneAt.toMillis() : 0),
      dir: 'desc'
    }
  },
  totalChanges: {
    key: 'totalChanges',
    items: {
      sort: (d) => {
        const s = d.data.stats;
        return s.added + s.changed + s.removed;
      },
      dir: 'desc'
    }
  },
  issues: {
    key: 'issues',
    items: {
      sort: (d) => d.data.stats.issues,
      dir: 'desc'
    }
  },
  effects: {
    key: 'effects',
    items: {
      sort: (d) => d.data.stats.effects,
      dir: 'desc'
    }
  }
};

const DEFAULT_SORTER = SORTERS.createdAt;

const ContentOrgUserImportsOverview = () => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  const { org } = useCurrentUser();
  const sideNav = useUsersSideNav();
  const [imports, loading] = useImportsByOrgId(org.id);
  const [hideNoChange, setHideNoChange] = useState<boolean>(false);

  const filteredImports = useMemo(() => {
    if (!hideNoChange || !imports) return imports;
    return imports.filter((i) => {
      const s = i.data.stats;
      return s.added + s.changed + s.removed > 0;
    });
  }, [imports, hideNoChange]);

  const defaultDir = DEFAULT_SORTER.items.dir;
  const { sortBy, direction: dir, setSort } = useSort<ColumnName>(
    'createdAt',
    defaultDir
  );
  const sorter = SORTERS[sortBy] || DEFAULT_SORTER;

  const otherProps = useMemo<O>(() => {
    const mostRecentImport = imports
      ? sortByLodash(
          imports.filter((i) => i.data.status !== UserImportStatus.ERROR),
          (i) => -i.data.createdAt.toMillis()
        )[0]
      : null;
    return { newestImportId: mostRecentImport?.id || null };
  }, [imports]);

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgUsersImportsOverview')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          <SyncButton org={org} />
          <SettingsButton org={org} />
        </PageToolbarActions>
      </PageToolbar>

      <FormControlLabel
        control={
          <Switch
            color="default" 
            checked={hideNoChange}
            onChange={(e) => setHideNoChange(e.target.checked)}
          />
        }
        label={t('Hide imports with no changes')}
        style={{ backgroundColor: hideNoChange ? '#FEFAEC' : undefined, alignSelf: 'flex-start', paddingLeft: 8, paddingRight: 16 }}
      />

      {loading && <Loader height={500} />}
      {filteredImports && filteredImports.length > 0 ? (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={filteredImports || []}
          rowToKey={ID_TO_ROW_KEY}
          sorter={sorter || DEFAULT_SORTER}
          sortDirection={sorter ? dir : defaultDir}
          renderHead={true}
          onHeadClick={(c, d) =>
            setSort(c.key as ColumnName, d || SORTERS[c.key].items.dir)
          }
          rowToHref={(d) => ROUTES.users.imports.details.url(d.id)}
          chunkSize={30}
          rootMargin="400px"
          otherProps={otherProps}
          rowHeight={ROW_HEIGHTS.dense}
          headProps={{ sticky: true, offset: 124 }}
        />
      ) : (
        <EmptyState
          title={t('No imports')}
          instruction={t('To create a new import, set up or run an import')}
        />
      )}
    </PageBody>
  );
};

/*
  - Add an additional integration
  - Setup integration
    - For google sheets, if no integration is configured, lead to this step first
    - Otherwise inform user about the creation of a new document which will be treated
    - As a data source
    - Fill the sheet with two sample users. A regular employee, and their manager
    - Tell people that they should feel free to remove these, they are just for reference
    - Let them check out the data source.

  - Button becomes: "Sync with Google Sheet" or "Pull from Google Sheet", "Import from Google Sheet"
    - Has a split option, which either holds several more options like Edit integration
    - or a settings cog which brings up an edit dialog, which would also allow removal

*/

export const PageOrgUserImportsOverview = () => {
  const { ROUTES } = useRoutes();
  const currentUser = useCurrentUser();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.imports.overview)) {
    return <NoAccess />;
  }

  return <ContentOrgUserImportsOverview />;
};
